import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import moment from 'moment';

import DatePicker from 'components/ui/DatePicker';
import { stationsShape } from 'constants/propTypes';
import { ActionButton } from 'components/ui/Button';
import { isTwoWayExchange } from 'utils/exchange';
import ReactTooltip from 'react-tooltip';
import {
  selectedProductsFromItemRefs,
  getBookingMagnetBoundDates
} from 'pages/BookingDetails/Journeys/AfterSalesCtas/AfterSalesCtas.utils';
import { productContainSubscriptionFare } from 'utils/subscriptionFares';
import serviceTypeCodes from 'constants/serviceTypeCodes';
import { getNSIndividualExchangeDate } from 'utils/summatedFares';

import {
  StyledHeading,
  StyledBookingMagnet,
  StyledJourneySelect,
  StyledStationSelect,
  StyledSwapButton
} from './SearchRequestBookingMagnet.styles';

class SearchRequestBookingMagnet extends Component {
  constructor(props) {
    super(props);

    const { outboundDate, inboundDate } = props.travelDates || {};
    const inboundTravelDate = inboundDate && moment(inboundDate);
    const outboundTravelDate = outboundDate && moment(outboundDate);

    this.state = {
      travelDate: outboundTravelDate || inboundTravelDate || null,
      travelDateErrorMsg: '',
      returnDate: isTwoWayExchange({ outboundDate, inboundDate }) ? inboundTravelDate : null,
      returnDateErrorMsg: '',
      shouldSwapStations: false
    };
  }

  handleTravelDateSelection = (_, date) => {
    const { travelDates: { outboundDate } = {}, outbound, inbound, bookingItems } = this.props;
    const fixedDate = getNSIndividualExchangeDate({
      segment: outboundDate ? outbound : inbound,
      selectedItemRefs: (bookingItems || '').split(',')
    });
    if (fixedDate && !date.isSame(fixedDate, 'day')) return;

    this.setState({ travelDate: date });
  };

  handleReturnDateSelection = (_, date) => {
    const fixedDate = getNSIndividualExchangeDate({
      segment: this.props.inbound,
      selectedItemRefs: (this.props.bookingItems || '').split(',')
    });
    if (fixedDate && !date.isSame(fixedDate, 'day')) return;

    this.setState({ returnDate: date });
  };

  handleClearTravelDatePicker = () => {
    this.setState({ travelDate: null });
  };

  handleClearReturnDatePicker = () => {
    this.setState({ returnDate: null });
  };

  setTravelDateErrorMessage = (message) => {
    this.setState({ travelDateErrorMsg: message });
  };

  setReturnDateErrorMessage = (message) => {
    this.setState({ returnDateErrorMsg: message });
  };

  handleSwapStations = () => {
    this.setState(({ shouldSwapStations }) => ({ shouldSwapStations: !shouldSwapStations }));
  };

  handleSubmit = (e) => {
    e.preventDefault();

    const { onSubmit, swapStations } = this.props;

    if (onSubmit) {
      const { travelDate, returnDate, shouldSwapStations } = this.state;

      const outDate = travelDate && travelDate.format('YYYY-MM-DD');
      const inDate = returnDate && returnDate.format('YYYY-MM-DD');

      onSubmit({
        travelDate: outDate,
        returnDate: inDate,
        hasSwapStations: shouldSwapStations ? !swapStations : swapStations
      });
    }
  };

  isSubmitDisabled() {
    const { travelDate, returnDate } = this.state;

    const canSubmitReturnDate = returnDate ? returnDate.isValid() : true;

    const valid = !!travelDate && travelDate.isValid() && canSubmitReturnDate;

    return !valid;
  }

  hasSubscriptionProduct(leg) {
    if (!leg) return true;
    const { bookingItems, isExchange } = this.props;
    if (!isExchange) return true;
    const selectedItems = (bookingItems || '').split(',');
    const productsFromItemRefs = selectedProductsFromItemRefs({ segments: leg, selectedItemRefs: selectedItems });
    return productContainSubscriptionFare({ products: productsFromItemRefs });
  }

  render() {
    const {
      origin,
      destination,
      returningOrigin,
      returningDestination,
      travelDates: { inboundDate, outboundDate } = {},
      stations,
      swapStations,
      shouldOfferSwapStations = false,
      reasonsSwapStationsIsDisabled = '',
      subscriptionStartDate,
      subscriptionEndDate,
      outbound = [],
      inbound = [],
      isExchangingSinglegOfAOutboundSummated,
      isExchangingSingleLegOfAInboundSummated
    } = this.props;

    const { travelDate, travelDateErrorMsg, returnDate, returnDateErrorMsg, shouldSwapStations } = this.state;

    const isStationSwapped = shouldSwapStations ? !swapStations : swapStations;

    const originStation = isStationSwapped ? destination : origin;
    const destinationStation = isStationSwapped ? origin : destination;
    const returningOriginStation = isStationSwapped ? returningDestination : returningOrigin;
    const returningDestinationStation = isStationSwapped ? returningOrigin : returningDestination;
    const isReturnJourney = outboundDate && inboundDate && returningOrigin && returningDestination;
    const directionForSingleJourney = inboundDate && !outboundDate ? 'inbound' : 'outbound';
    const selectedItemRefs = (this.props.bookingItems || '').split(',');
    const nsOutboundExchangeDate = getNSIndividualExchangeDate({ segment: outbound, selectedItemRefs });
    const nsInboundExchangeDate = getNSIndividualExchangeDate({ segment: inbound, selectedItemRefs });
    const fixedTravelDate = directionForSingleJourney === 'outbound' ? nsOutboundExchangeDate : nsInboundExchangeDate;

    const isExchangingSingleOutboundLegOfSNCBSummated =
      isExchangingSinglegOfAOutboundSummated &&
      outbound?.filter((o) => !o.cancelled)?.some((item) => item.serviceType?.code === serviceTypeCodes.SNCB);
    const isExchangingSingleInboundLegOfSNCBSummated =
      isExchangingSingleLegOfAInboundSummated &&
      inbound?.filter((o) => !o.cancelled)?.some((item) => item.serviceType?.code === serviceTypeCodes.SNCB);

    return (
      <>
        <form onSubmit={this.handleSubmit} data-testid="magnet-form">
          <StyledBookingMagnet>
            <StyledJourneySelect data-testid="single-journey-select" data-cy="single-journey-select">
              <StyledHeading>
                {isReturnJourney || directionForSingleJourney === 'outbound' ? (
                  <FormattedMessage id="exchange.bookingSearchOutboundTitle" />
                ) : (
                  <FormattedMessage id="exchange.bookingSearchInboundTitle" />
                )}
              </StyledHeading>

              <StyledStationSelect
                label={<FormattedMessage id="bookingMagnet.from" />}
                disabled
                value={originStation}
                stations={stations}
                id="origin"
              />

              <StyledStationSelect
                label={<FormattedMessage id="bookingMagnet.to" />}
                disabled
                value={destinationStation}
                stations={stations}
                id="destination"
              />

              <DatePicker
                id="travel-date"
                label={<FormattedMessage id="bookingMagnet.departFrom" />}
                errorMessage={travelDateErrorMsg}
                setErrorMessage={this.setTravelDateErrorMessage}
                value={travelDate}
                minDate={getBookingMagnetBoundDates({
                  fixedExchangeDate: fixedTravelDate,
                  hasSubscriptionProductOutbound: this.hasSubscriptionProduct(outbound),
                  hasSubscriptionProductInbound: this.hasSubscriptionProduct(inbound),
                  isMinDate: true,
                  isReturnJourney,
                  directionForSingleJourney,
                  subscriptionDate: subscriptionStartDate,
                  outbound,
                  inbound,
                  isExchangingSingleOutboundLegOfSNCBSummated,
                  isExchangingSingleInboundLegOfSNCBSummated
                })}
                maxDate={getBookingMagnetBoundDates({
                  fixedExchangeDate: fixedTravelDate,
                  hasSubscriptionProductOutbound: this.hasSubscriptionProduct(outbound),
                  hasSubscriptionProductInbound: this.hasSubscriptionProduct(inbound),
                  isMinDate: false,
                  isReturnJourney,
                  directionForSingleJourney,
                  subscriptionDate: subscriptionEndDate,
                  outbound,
                  inbound,
                  isExchangingSingleOutboundLegOfSNCBSummated,
                  isExchangingSingleInboundLegOfSNCBSummated
                })}
                setTravelDate={this.handleTravelDateSelection}
                clearTravelDate={this.handleClearTravelDatePicker}
              />
            </StyledJourneySelect>

            {isReturnJourney && (
              <StyledJourneySelect data-testid="return-journey-select" data-cy="return-journey-select">
                <StyledHeading>
                  <FormattedMessage id="exchange.bookingSearchInboundTitle" />
                </StyledHeading>

                <StyledStationSelect
                  label={<FormattedMessage id="bookingMagnet.from" />}
                  disabled
                  value={returningOriginStation}
                  stations={stations}
                  id="return-origin"
                />

                <StyledStationSelect
                  label={<FormattedMessage id="bookingMagnet.to" />}
                  disabled
                  value={returningDestinationStation}
                  stations={stations}
                  id="return-destination"
                />

                <DatePicker
                  id="return-date"
                  label={<FormattedMessage id="bookingMagnet.departFrom" />}
                  errorMessage={returnDateErrorMsg}
                  setErrorMessage={this.setReturnDateErrorMessage}
                  value={returnDate}
                  minDate={getBookingMagnetBoundDates({
                    fixedExchangeDate: nsInboundExchangeDate,
                    isMinDate: true,
                    directionForSingleJourney: 'inbound',
                    subscriptionDate: subscriptionStartDate,
                    hasSubscriptionProductInbound: this.hasSubscriptionProduct(inbound),
                    inbound,
                    isExchangingSingleInboundLegOfSNCBSummated
                  })}
                  maxDate={getBookingMagnetBoundDates({
                    fixedExchangeDate: nsInboundExchangeDate,
                    isMinDate: false,
                    directionForSingleJourney: 'inbound',
                    subscriptionDate: subscriptionEndDate,
                    hasSubscriptionProductInbound: this.hasSubscriptionProduct(inbound),
                    inbound,
                    isExchangingSingleInboundLegOfSNCBSummated
                  })}
                  setTravelDate={this.handleReturnDateSelection}
                  clearTravelDate={this.handleClearReturnDatePicker}
                />
              </StyledJourneySelect>
            )}
          </StyledBookingMagnet>

          {shouldOfferSwapStations ? (
            <span data-for="swap-button" data-tip>
              <StyledSwapButton
                onClick={this.handleSwapStations}
                disabled={reasonsSwapStationsIsDisabled !== ''}
                type="button"
                id="swap-button"
              >
                <FormattedMessage id="exchange.swapStations" />
              </StyledSwapButton>
              {reasonsSwapStationsIsDisabled !== '' && (
                <ReactTooltip
                  className="tooltip"
                  id="swap-button"
                  data-cy="swap-button-tooltip"
                  data-testId="swap-button-tooltip"
                  place="top"
                >
                  <FormattedMessage id={reasonsSwapStationsIsDisabled} />
                </ReactTooltip>
              )}
            </span>
          ) : null}

          <ActionButton onClick={this.handleSubmit} disabled={this.isSubmitDisabled()} id="submit-search">
            <FormattedMessage id="exchange.updateSearch" />
          </ActionButton>
        </form>
      </>
    );
  }
}

SearchRequestBookingMagnet.propTypes = {
  origin: PropTypes.string,
  destination: PropTypes.string,
  returningOrigin: PropTypes.string,
  returningDestination: PropTypes.string,
  travelDates: PropTypes.shape({
    inboundDate: PropTypes.string,
    outboundDate: PropTypes.string
  }),
  stations: stationsShape,
  onSubmit: PropTypes.func,
  swapStations: PropTypes.bool,
  shouldOfferSwapStations: PropTypes.bool,
  reasonsSwapStationsIsDisabled: PropTypes.string,
  subscriptionStartDate: PropTypes.string,
  subscriptionEndDate: PropTypes.string,
  bookingItems: PropTypes.string,
  isExchange: PropTypes.bool,
  outbound: PropTypes.array,
  inbound: PropTypes.array,
  isExchangingSinglegOfAOutboundSummated: PropTypes.bool,
  isExchangingSingleLegOfAInboundSummated: PropTypes.bool
};

export default SearchRequestBookingMagnet;
